import { isNativeAppWebview } from 'core/utils/webapp';
import React, { KeyboardEvent, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectCustomizableOption } from './SelectCustomizable';

let globalSelectCounter = 0;

type ActionMeta = { action: string; removedValue?: SelectCustomizableOption };

type StyleFn = (base: Record<string, unknown>, state?: Record<string, unknown>) => Record<string, unknown>;

type SharedStyles = {
  menu: StyleFn;
  menuPortal: StyleFn;
  control: StyleFn;
  option: StyleFn;
};

type SelectStyles = SharedStyles & {
  valueContainer?: StyleFn;
};

type SharedProps = {
  _listboxId: string;
  _focusedOptionId: string | null;
  _onOptionFocusChange: (id: string) => void;
  _errorId: string | undefined;
  _hasError: boolean;
  _required: boolean;
  _removeLabel: string;
  onKeyDown: (event: KeyboardEvent) => void;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  tabSelectsValue: boolean;
  iconTypeIndicator: string | undefined;
  showValuesAsList: boolean;
  showValuesAsText: boolean;
  useShortLabels: boolean | undefined;
};

type UseSelectCustomizableParams = {
  inputId?: string;
  name?: string;
  isSearchable?: boolean;
  error?: string;
  noOptionsMessage?: string;
  label?: ReactNode;
  ariaLabel?: string;
  placeholder?: ReactNode;
  required?: boolean;
  variant?: 'default' | 'subtle';
  size?: 'default' | 'small';
  tagSize?: 'sm' | 'md';
  iconTypeIndicator?: string;
  multiValueDisplay?: 'inline' | 'sortedList';
  showValuesAsText?: boolean;
  useShortLabels?: boolean;
  onChange?: (value: unknown, actionMeta?: ActionMeta) => void;
  onCreateOption?: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
};

type UseSelectCustomizableReturn = {
  resolvedInputId: string;
  labelId: string;
  listboxId: string;
  errorId: string;
  selectRef: MutableRefObject<unknown>;
  finalIsSearchable: boolean | undefined;
  finalMenuPlacement: 'top' | 'auto';
  getNoOptionsMessage: () => string;
  getCreateLabel: (text: string) => string;
  getScreenReaderStatus: (state: { count: number }) => string;
  sharedStyles: SharedStyles;
  selectStyles: SelectStyles;
  resolvedPlaceholder: ReactNode;
  resolvedAriaLabel: string | undefined;
  labelTitle: string;
  className: string;
  isSortedList: boolean;
  sharedProps: SharedProps;
  handleCreatableChange: (newValue: unknown, actionMeta?: ActionMeta) => void;
  formatGroupLabel: (data: { label: string; options: unknown[] }) => React.ReactElement;
  getOptionLabel: (data: SelectCustomizableOption, meta: { context: string }) => ReactNode;
};

type SelectInstance = {
  openMenu?: (position: string) => void;
};

type SelectRefValue = {
  select?: SelectInstance & { select?: SelectInstance };
};

function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIPad = /ipad/.test(userAgent) || (navigator.maxTouchPoints > 1 && /macintosh/.test(userAgent));
  const isMobile = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  return isIPad || isMobile || isNativeAppWebview();
}

function getControlBorderStyles(hasError: boolean): Record<string, unknown> {
  if (hasError) {
    return {
      border: '2px solid var(--select-customizable-border-error)',
      '&:hover': { borderColor: 'var(--select-customizable-border-error)' },
    };
  }
  return {
    border: '1px solid var(--select-customizable-border-default)',
    '&:hover': { border: '2px solid var(--select-customizable-border-hover)' },
  };
}

function getInnerSelectInstance(ref: MutableRefObject<unknown>): SelectInstance | null {
  if (!ref.current) return null;
  // ref.current = StateManager instance
  // ref.current.select = inner Select (regular) OR Creatable (creatable)
  // ref.current.select.select = inner Select (creatable only)
  const { select: inner } = ref.current as SelectRefValue;
  if (!inner) return null;
  return inner.select ?? inner;
}

// Aviso en desarrollo: el select cae al placeholder como nombre accesible
// cuando no hay `label` ni `aria-label`. Es un stopgap; lo suyo es un nombre
// específico y con sentido para el campo (ver "Deuda técnica" en la mdx).
function warnMissingAccessibleName(): void {
  if (process.env.NODE_ENV === 'production') return;
  console.warn(
    '[SelectCustomizable] Sin `label` ni `aria-label`: el nombre accesible cae al `placeholder`. ' +
      'Pasa un `label` visible o un `aria-label` específico y con sentido para el campo.'
  );
}

function getLabelTitle(labelValue: ReactNode): string {
  if (typeof labelValue === 'string') return labelValue;
  if (!React.isValidElement(labelValue)) return '';

  const { children } = labelValue.props as { children?: ReactNode };
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.filter((child) => typeof child === 'string').join(' ');

  return '';
}

export default function useSelectCustomizable({
  inputId,
  name,
  isSearchable,
  error,
  noOptionsMessage,
  label,
  ariaLabel,
  placeholder,
  required,
  variant = 'default',
  size = 'default',
  tagSize = 'md',
  iconTypeIndicator,
  multiValueDisplay = 'inline',
  showValuesAsText = false,
  useShortLabels,
  onChange,
  onCreateOption,
  onKeyDown: consumerOnKeyDown,
  onMenuOpen: consumerOnMenuOpen,
  onMenuClose: consumerOnMenuClose,
}: UseSelectCustomizableParams): UseSelectCustomizableReturn {
  const { t } = useTranslation(['placeholder']);

  const instanceIdRef = useRef(`select-accessible-${++globalSelectCounter}`);
  const resolvedInputId = inputId || name || instanceIdRef.current;
  const labelId = `${resolvedInputId}-label`;
  const listboxId = `${resolvedInputId}-listbox`;
  const errorId = `${resolvedInputId}-error`;

  const selectRef = useRef<unknown>(null);
  const menuIsOpenRef = useRef(false);
  const [focusedOptionId, setFocusedOptionId] = useState<string | null>(null);

  const isOnMobileDevice = useMemo(isMobileDevice, []);
  const finalIsSearchable = isOnMobileDevice ? false : isSearchable;
  const finalMenuPlacement: 'top' | 'auto' = isOnMobileDevice ? 'top' : 'auto';

  const isSortedList = multiValueDisplay === 'sortedList';

  // Nombre accesible: si hay label visible manda el aria-labelledby; si no,
  // usa el aria-label del consumidor y, en su defecto, cae al placeholder
  // (que siempre es un string) para que el combobox nunca quede sin nombre.
  // `||` (no `??`) para que un placeholder="" también caiga al default.
  const resolvedPlaceholder = placeholder || t('Select');
  const placeholderName = typeof resolvedPlaceholder === 'string' ? resolvedPlaceholder : undefined;
  const resolvedAriaLabel = ariaLabel ?? (label ? undefined : placeholderName);
  const labelTitle = getLabelTitle(label);

  const hasAccessibleName = Boolean(label) || Boolean(ariaLabel);
  useEffect(() => {
    if (!hasAccessibleName) warnMissingAccessibleName();
  }, [hasAccessibleName]);

  const className = `select-customizable select-customizable--${variant} select-customizable--size-${size} select-customizable--tag-${tagSize}${
    error ? ' select-customizable--error' : ''
  }`;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter' && !menuIsOpenRef.current) {
      event.preventDefault();
      const instance = getInnerSelectInstance(selectRef);
      if (instance && typeof instance.openMenu === 'function') {
        instance.openMenu('first');
      }
    }
  }, []);

  const handleMenuOpen = useCallback(() => {
    menuIsOpenRef.current = true;
  }, []);

  const handleMenuClose = useCallback(() => {
    menuIsOpenRef.current = false;
    setFocusedOptionId(null);
  }, []);

  const onOptionFocusChange = useCallback((id: string) => {
    setFocusedOptionId(id || null);
  }, []);

  const composedKeyDown = useCallback(
    (event: KeyboardEvent) => {
      handleKeyDown(event);
      if (consumerOnKeyDown) consumerOnKeyDown(event);
    },
    [handleKeyDown, consumerOnKeyDown]
  );

  const composedMenuOpen = useCallback(() => {
    handleMenuOpen();
    if (consumerOnMenuOpen) consumerOnMenuOpen();
  }, [handleMenuOpen, consumerOnMenuOpen]);

  const composedMenuClose = useCallback(() => {
    handleMenuClose();
    if (consumerOnMenuClose) consumerOnMenuClose();
  }, [handleMenuClose, consumerOnMenuClose]);

  // react-select v3 Creatable tiene un bug de igualdad por referencia: cuando el
  // menú se re-renderiza entre el hover y el click (frecuente al escribir), la
  // referencia de la opción clicada deja de coincidir con state.newOption, y la
  // opción nueva se cuela por onChange en vez de disparar onCreateOption. Se
  // reenvía cualquier valor __isNew__ a onCreateOption.
  const handleCreatableChange = useCallback(
    (newValue: unknown, actionMeta?: ActionMeta) => {
      const candidate = Array.isArray(newValue) ? newValue.at(-1) : (newValue as SelectCustomizableOption | null);
      if (candidate?.__isNew__ && onCreateOption) {
        onCreateOption(String(candidate.value));
        return;
      }
      if (onChange) onChange(newValue, actionMeta);
    },
    [onChange, onCreateOption]
  );

  const getNoOptionsMessage = useCallback(() => {
    return noOptionsMessage ?? t('placeholder:No options');
  }, [noOptionsMessage, t]);

  // Solo se anuncia el recuento con el menú abierto; cerrado, react-select daría
  // un "0 results available" falso. Texto en inglés (i18n fuera de alcance).
  const getScreenReaderStatus = useCallback(({ count }: { count: number }): string => {
    if (menuIsOpenRef.current) return `${count} result${count === 1 ? '' : 's'} available`;
    return '';
  }, []);

  const getCreateLabel = useCallback((text: string) => `${t('placeholder:Create new section')}: ${text}`, [t]);

  const formatGroupLabel = useCallback(
    (data: { label: string; options: unknown[] }): React.ReactElement => (
      <div className="select-customizable__group-label">
        <span>{data.label}</span>
        <span className="select-customizable__group-badge">{data.options.length}</span>
      </div>
    ),
    []
  );

  const getOptionLabel = useCallback(
    (data: SelectCustomizableOption, { context }: { context: string }): ReactNode =>
      useShortLabels && context === 'value' ? data.shortLabel : data.label,
    [useShortLabels]
  );

  const sharedStyles = useMemo<SharedStyles>(
    () => ({
      menu: (provided) => ({ ...provided, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      control: (provided) => ({
        ...provided,
        boxShadow: undefined,
        cursor: 'pointer',
        borderRadius: 'var(--select-customizable-border-radius)',
        transition: 'border-color 0.4s',
        ...getControlBorderStyles(!!error),
      }),
      option: (provided) => ({
        ...provided,
        backgroundColor: 'transparent',
        color: 'inherit',
      }),
    }),
    [error]
  );

  const selectStyles = useMemo<SelectStyles>(() => {
    if (!isSortedList) return sharedStyles;
    return {
      ...sharedStyles,
      control: (base, state = {}) => {
        const shared = sharedStyles.control(base, state);
        const { hasValue, isFocused } = state as { hasValue?: boolean; isFocused?: boolean };
        if (hasValue && !isFocused) {
          return {
            ...shared,
            border: '1px solid var(--select-customizable-border-default)',
            boxShadow: 'none',
          };
        }
        return shared;
      },
      valueContainer: (base) => ({
        ...base,
        padding: '2px 8px',
        overflow: 'hidden',
        cursor: 'pointer',
      }),
    };
  }, [isSortedList, sharedStyles]);

  const sharedProps = useMemo<SharedProps>(
    () => ({
      _listboxId: listboxId,
      _focusedOptionId: focusedOptionId,
      _onOptionFocusChange: onOptionFocusChange,
      _errorId: error ? errorId : undefined,
      _hasError: !!error,
      _required: !!required,
      _removeLabel: t('placeholder:Remove'),
      onKeyDown: composedKeyDown,
      onMenuOpen: composedMenuOpen,
      onMenuClose: composedMenuClose,
      tabSelectsValue: false,
      // Props que consumen los componentes custom vía selectProps
      iconTypeIndicator,
      showValuesAsList: isSortedList,
      showValuesAsText,
      useShortLabels,
    }),
    [
      listboxId,
      focusedOptionId,
      onOptionFocusChange,
      error,
      errorId,
      required,
      t,
      composedKeyDown,
      composedMenuOpen,
      composedMenuClose,
      iconTypeIndicator,
      isSortedList,
      showValuesAsText,
      useShortLabels,
    ]
  );

  return {
    resolvedInputId,
    labelId,
    listboxId,
    errorId,
    selectRef,
    finalIsSearchable,
    finalMenuPlacement,
    getNoOptionsMessage,
    getCreateLabel,
    getScreenReaderStatus,
    sharedStyles,
    selectStyles,
    resolvedPlaceholder,
    resolvedAriaLabel,
    labelTitle,
    className,
    isSortedList,
    sharedProps,
    handleCreatableChange,
    formatGroupLabel,
    getOptionLabel,
  };
}
